/**
 * Region-aware free shipping progress bar.
 *
 * Resolves the visitor's region through MandalasGeo (IP geolocation, see
 * snippets/geo-region-init.liquid), then renders the <shipping-bar> elements
 * with that region's threshold and cap — each configured in the region's own
 * currency (SGD 100, MYR 250, PHP 2500, USD 100, …). The cart total arrives in
 * store currency, so it is converted with the region's exchange rate before
 * being compared and displayed. Overrides the theme's global
 * updateFreeShipping so the bar stays in sync on every cart change.
 *
 * A region whose threshold is 0 (or missing) hides the bar entirely.
 */
(function () {
  var cfg = window.__freeShippingGeoConfig;
  if (!cfg || !cfg.enabled) return;

  var region = null; // resolved region key once detection completes

  // Shopify-standard money formatter (handles thousands grouping correctly,
  // unlike the theme's formatPrice which drops separators for some formats).
  function formatMoney(cents, format) {
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '${{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = fmt.match(placeholder);
    var token = match ? match[1] : 'amount';
    var value;
    switch (token) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ''); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      default: value = withDelimiters(cents, 2); break;
    }
    return fmt.replace(placeholder, value);
  }

  function storeMoneyFormat() {
    try {
      return window.theme.settings.money_format;
    } catch (e) {
      return null;
    }
  }

  // Amounts are in the region's own currency, so use its format. A blank
  // format falls back to the store's (correct for the store-currency region).
  function money(cents, rc) {
    return formatMoney(cents, (rc && rc.format) || storeMoneyFormat());
  }

  function fill(tpl, amountCents, rc) {
    return (tpl || '').split('{{amount}}').join(money(amountCents, rc));
  }

  function num(value, fallback) {
    var n = parseFloat(value);
    return isFinite(n) ? n : fallback;
  }

  function regionConfig() {
    if (!region) return null;
    var rc = cfg.regions && cfg.regions[region];
    if (!rc) return null;
    // A zero threshold is how a merchant opts a region out of the promo.
    return num(rc.threshold, 0) > 0 ? rc : null;
  }

  function renderBars(total_price, item_count) {
    var bars = document.querySelectorAll('shipping-bar');
    if (!bars.length) return;

    // Region not resolved yet → leave the bar hidden rather than show a
    // threshold that may belong to the wrong country.
    if (region === null) return;

    var rc = regionConfig();

    bars.forEach(function (b) { b.setAttribute('data-geo-ready', ''); });

    // Region opted out of the promo → hide the bar entirely.
    if (!rc) {
      bars.forEach(function (b) { b.style.display = 'none'; });
      return;
    }

    // total_price is in the currency the storefront is presenting; undo that
    // to get store currency, then convert to the region's currency.
    var presentmentRate = (window.Shopify && Shopify.currency && Shopify.currency.rate)
      ? parseFloat(Shopify.currency.rate) : 1;
    var storeCents = total_price / (presentmentRate > 0 ? presentmentRate : 1);
    var rate = num(rc.rate, 1);
    if (rate <= 0) rate = 1;

    var cartTotal = storeCents / rate;                 // region currency, in cents
    var threshold = Math.round(num(rc.threshold, 0) * 100);
    var cap = Math.round(num(rc.cap, 0) * 100);
    var remaining = Math.max(0, threshold - cartTotal);

    bars.forEach(function (bar) {
      // Keep the bar visible even when the cart is empty so it can nudge the
      // shopper to start adding products.
      bar.style.display = '';
      bar.style.visibility = 'visible';

      var textEl = bar.querySelector('.progress-bar__text');
      var inner = bar.querySelector('[data-progress-line]');
      var note = bar.querySelector('[data-progress-note]');
      var pct = Math.max(0, Math.min(100, (cartTotal / threshold) * 100));

      if (item_count === 0) {
        // Empty cart: encourage the shopper, show the full threshold, empty bar.
        if (textEl) textEl.innerHTML = fill(cfg.text.empty, threshold, rc);
        if (inner) inner.style.width = '0%';
        if (note) { note.hidden = true; note.innerHTML = ''; }
        bar.classList.remove('is-unlocked');
      } else if (cartTotal >= threshold) {
        if (textEl) textEl.innerHTML = cfg.text.success;
        if (inner) inner.style.width = '100%';
        if (note) {
          note.innerHTML = fill(cfg.text.cap, cap, rc);
          note.hidden = false;
        }
        bar.classList.add('is-unlocked');
      } else {
        if (textEl) textEl.innerHTML = fill(cfg.text.progress, remaining, rc);
        if (inner) inner.style.width = 'calc(' + pct + '% + 2px)';
        if (note) { note.hidden = true; note.innerHTML = ''; }
        bar.classList.remove('is-unlocked');
      }
    });
  }

  function geoUpdate() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (c) { renderBars(c.total_price, c.item_count); })
      .catch(function () {});
  }

  // Take over the theme's cart-sync hook so every cart change re-renders the bar.
  window.updateFreeShipping = geoUpdate;

  function start() {
    if (!window.MandalasGeo) return;
    window.MandalasGeo.getRegion().then(function (r) {
      region = r;
      geoUpdate();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
